import React from "react";
import MatchesTab from "../profile/MatchesTab";
import { primePoster, type ReplayPoster } from "../../shared/replays";
import type { MatchHistoryRow } from "../../shared/rating/store";

// /debug/matches: the profile's Matches tab on fixture data (two sessions, every
// kind of card: a win, a gain, a loss, a match with no poster, one past its replay),
// for iterating on it without an account that has played. Not linked in the UI.

const COLORS = ["blue", "pink", "orange", "purple", "green", "turquoise", "yellow", "red", "lime", "indigo"];
const NAMES = ["You", "GapHunter", "CometKid", "TurnLeftPls", "Loopy", "Slytherin", "MadDasher", "WormLord", "Zigzag", "Curvy"];

function fixturePoster(seed: number, seats: number, round: boolean, size: number, myWins: number[], rounds: number): ReplayPoster {
  let s = seed;
  const r = () => { s = (s + 0x6d2b79f5) | 0; let t = Math.imul(s ^ (s >>> 15), 1 | s); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  const trails = Array.from({ length: seats }, (_, w) => {
    let a = r() * Math.PI * 2, x = size / 2 + Math.cos(a) * size * (0.1 + r() * 0.3), y = size / 2 + Math.sin(a) * size * (0.1 + r() * 0.3), dir = r() * Math.PI * 2, turn = 0, target = 0;
    const lines: number[][] = [[]];
    const life = 40 + Math.floor(r() * 110);
    for (let i = 0; i < life; i++) {
      if (i % 12 === 0) target = (r() - 0.5) * 0.22;
      turn += (target - turn) * 0.3;
      const dx = x - size / 2, dy = y - size / 2, edge = round ? Math.hypot(dx, dy) / (size * 0.47) : Math.max(Math.abs(dx), Math.abs(dy)) / (size * 0.46);
      if (edge > 0.8) { const home = Math.atan2(-dy, -dx); turn += Math.atan2(Math.sin(home - dir), Math.cos(home - dir)) * 0.2; }
      dir += turn; x += Math.cos(dir) * size / 50; y += Math.sin(dir) * size / 50;
      if (i % 17 === 16) lines.push([]); else lines[lines.length - 1].push(Math.round(x), Math.round(y));
    }
    return { c: COLORS[w % COLORS.length], l: lines.filter((l) => l.length >= 4) };
  });
  const ids = Array.from({ length: seats }, (_, i) => "player_" + i);
  return {
    v: 1,
    map: { name: null, width: size, height: size, round },
    rounds: Array.from({ length: rounds }, (_, i) => { const winner = myWins.includes(i) ? ids[0] : ids[1 + Math.floor(r() * (seats - 1))]; return { winner, order: [winner, ...ids.filter((id) => id !== winner)] }; }),
    seats: ids.map((id, i) => ({ id, colorId: COLORS[i % COLORS.length], name: NAMES[i % NAMES.length], ...(i === 0 ? { tag: "debug" } : {}) })),
    trails,
  };
}

const at = (daysAgo: number, h: number, m: number) => { const d = new Date(); d.setDate(d.getDate() - daysAgo); d.setHours(h, m, 0, 0); return d.toISOString(); };
const row = (matchId: string, createdAt: string, placement: number, fieldSize: number, map: string, mu: [number, number], playstyle = "Classic"): MatchHistoryRow => ({ matchId, createdAt, placement, fieldSize, map, playstyle, muBefore: mu[0], muAfter: mu[1] });

// oldest first, like the real fetch
const HISTORY: MatchHistoryRow[] = [
  row("public:small#1@1", at(45, 20, 10), 2, 4, "Square 620", [24.0, 24.3]),
  row("public:large#2@2", at(1, 22, 10), 5, 20, "Circle 1400", [24.3, 24.5]),
  row("public:small#3@3", at(1, 22, 41), 2, 5, "Square 620", [24.5, 25.1]),
  row("public:medium#4@4", at(1, 23, 2), 6, 10, "Square 1000", [25.1, 24.5]),
  row("public:small#5@5", at(0, 21, 12), 4, 5, "Circle 620", [24.5, 23.8]),
  row("public:medium#6@6", at(0, 21, 40), 3, 8, "Square 1000", [23.8, 24.3], "Sabotage"),
  row("public:large#7@7", at(0, 22, 15), 1, 25, "Circle 1400", [24.3, 26.2]),
];
primePoster("public:large#2@2", fixturePoster(11, 20, true, 1420, [], 6));
primePoster("public:small#3@3", fixturePoster(12, 5, false, 640, [1, 4], 7));
primePoster("public:medium#4@4", null); // played, but its poster is missing: still lists
primePoster("public:small#5@5", fixturePoster(14, 5, true, 640, [], 6));
primePoster("public:medium#6@6", fixturePoster(15, 8, false, 1020, [2], 7));
primePoster("public:large#7@7", fixturePoster(16, 25, true, 1420, [1, 3, 5], 6));

export default function DebugMatches() {
  return (
    <div className="page-main" style={{ padding: 24, maxWidth: 860 }}>
      <h1 style={{ fontFamily: "Orbitron, sans-serif", color: "#eaf0ff", fontSize: 22 }}>Profile · Matches (fixtures)</h1>
      <MatchesTab history={HISTORY} loading={false} accountId={null} debugTag="debug" />
    </div>
  );
}
